import React from 'react';
import { Icon } from 'react-icons-kit';
import { ic_arrow_upward } from 'react-icons-kit/md/ic_arrow_upward';
import { ic_arrow_downward } from 'react-icons-kit/md/ic_arrow_downward';

export type SortDirection = 'asc' | 'desc';

// Which way a "Sort By" value runs on one column, or null when the table is
// sorted by something else. Values follow the API's ordering: `field` is
// ascending, `-field` descending.
export const sortDirectionFor = (
  ordering: string,
  field: string,
): SortDirection | null =>
  ordering === field ? 'asc' : ordering === `-${field}` ? 'desc' : null;

// aria-sort for a column header, so screen readers hear what the arrow shows.
export const ariaSortFor = (
  direction: SortDirection | null,
): 'ascending' | 'descending' | undefined =>
  direction === 'asc'
    ? 'ascending'
    : direction === 'desc'
      ? 'descending'
      : undefined;

// A Material arrow next to the heading of the column a table is sorted by, in
// the header's own text colour. Up for A–Z / oldest first, down for Z–A / newest
// first. It only reports the sort; "Sort By" above the table still sets it.
const SortArrow = ({ direction }: { direction: SortDirection | null }) =>
  direction ? (
    <Icon
      icon={direction === 'asc' ? ic_arrow_upward : ic_arrow_downward}
      size={16}
      // The icon's wrapper otherwise takes the text's line height and sits the
      // glyph below the middle of the row.
      style={{ display: 'flex' }}
      aria-hidden="true"
      data-testid={`SortArrow-${direction}`}
    />
  ) : null;

// A column heading with its sort arrow: one centred row, so the arrow sits on
// the same axis as the text whatever the line height.
export const SortableHeading = ({
  direction,
  children,
}: {
  direction: SortDirection | null;
  children: React.ReactNode;
}) => (
  <span className="inline-flex items-center gap-1">
    {children}
    <SortArrow direction={direction} />
  </span>
);

export default SortArrow;
